import { t } from "./i18n";
import { createInlineOverlay } from "./utils";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

const EXTENDED_EMOJIS = [
  "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "😉", "😊", "😇",
  "🥰", "😍", "🤩", "😘", "😗", "😚", "😋", "😛", "😜", "🤪", "😝", "🤑",
  "🤗", "🤭", "🤫", "🤔", "🤐", "🤨", "😐", "😑", "😶", "😏", "😒", "🙄",
  "😬", "🤥", "😌", "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕", "🤢", "🤮",
  "🥵", "🥶", "🥴", "😵", "🤯", "🤠", "🥳", "🥸", "😎", "🤓", "🧐", "😕",
  "😟", "🙁", "😮", "😯", "😲", "😳", "🥺", "😦", "😧", "😨", "😰", "😥",
  "😢", "😭", "😱", "😖", "😣", "😞", "😓", "😩", "😫", "🥱", "😤", "😡",
  "😠", "🤬", "😈", "👿", "💀", "💩", "🤡", "👹", "👺", "👻", "👽", "🤖",
  "👋", "🤚", "🖐", "✋", "🖖", "👌", "🤌", "🤏", "✌️", "🤞", "🤟", "🤘",
  "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👍", "👎", "✊", "👊", "🤛",
  "🤜", "👏", "🙌", "👐", "🤲", "🤝", "🙏", "💪", "🦾",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❤️‍🔥",
  "💯", "💢", "💥", "💫", "💦", "💨", "🕳", "💣", "💬", "🔥", "⭐", "🌟",
  "✨", "💖", "💝", "💘", "💗", "💓", "💞", "💕",
  "🎉", "🎊", "🎈", "🎁", "🎀", "🏆", "🥇", "🥈", "🥉", "⚽", "🏀", "🎯",
  "🎮", "🎲", "🎵", "🎶", "🎤", "🎸", "🎹", "📱", "💻", "⌨️", "📸", "📝",
  "🍕", "🍔", "🍟", "🌮", "🍣", "🍪", "🍰", "🎂", "🍫", "☕", "🍺", "🥂",
  "🌹", "🌸", "🌻", "🌈", "☀️", "🌙", "⭐", "🌊", "🔥", "❄️",
];

export function sendReadReceiptForRoom(client, room) {
  try {
    const events = room.getLiveTimeline().getEvents();
    if (events.length === 0) return;
    
    // Find the latest event that is actually on the server (not a local echo starting with ~)
    let lastEvent = null;
    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].getId() && !events[i].getId().startsWith("~")) {
        lastEvent = events[i];
        break;
      }
    }
    
    if (lastEvent) {
      client.sendReadReceipt(lastEvent).catch(() => {});
    }
  } catch (_) { }
}

export function findUserReaction(client, roomId, targetEventId) {
  const room = client.getRoom(roomId);
  if (!room) return null;

  const myUserId = client.getUserId();
  const events = room.getLiveTimeline().getEvents();

  let latestReaction = null;

  for (const ev of events) {
    if (ev.getType() !== "m.reaction") continue;
    if (ev.isRedacted()) continue;
    const rel = ev.getContent()["m.relates_to"];
    if (rel?.event_id !== targetEventId) continue;
    if (ev.getSender() !== myUserId) continue;

    latestReaction = {
      emoji: rel.key,
      reactionEventId: ev.getId(),
    };
  }

  return latestReaction;
}

export async function toggleReaction(client, roomId, targetEventId, emoji) {
  const current = findUserReaction(client, roomId, targetEventId);

  try {
    if (current) {
      await client.redactEvent(roomId, current.reactionEventId);
      if (current.emoji === emoji) return;
    }

    await client.sendEvent(roomId, "m.reaction", {
      "m.relates_to": {
        rel_type: "m.annotation",
        event_id: targetEventId,
        key: emoji,
      },
    });
  } catch (err) {
    console.error("[BryghtChat] Reaction toggle failed:", err);
  }
}

export function showReactionPicker(roomId, eventId, client, anchorEl) {
  const currentReaction = findUserReaction(client, roomId, eventId);

  const picker = document.createElement("div");
  picker.className = "bryght-reaction-picker";

  const quickRow = document.createElement("div");
  quickRow.className = "bryght-reaction-picker-row";

  let overlayHandle = null;

  QUICK_REACTIONS.forEach((emoji) => {
    const btn = document.createElement("button");
    btn.className = "bryght-reaction-picker-btn";
    if (currentReaction?.emoji === emoji) {
      btn.classList.add("active");
    }
    btn.textContent = emoji;
    btn.addEventListener("click", async () => {
      if (overlayHandle) overlayHandle.close();
      await toggleReaction(client, roomId, eventId, emoji);
    });
    quickRow.appendChild(btn);
  });

  const moreBtn = document.createElement("button");
  moreBtn.className = "bryght-reaction-picker-btn bryght-reaction-more";
  moreBtn.textContent = "＋";
  moreBtn.title = t("more_emojis", "Weitere Emojis");
  moreBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    showFullEmojiPicker(picker, roomId, eventId, client, currentReaction, overlayHandle);
  });
  quickRow.appendChild(moreBtn);

  picker.appendChild(quickRow);

  overlayHandle = createInlineOverlay({
    anchor: anchorEl,
    content: picker,
    group: "reaction-picker",
    align: "right",
  });
}

export function showFullEmojiPicker(picker, roomId, eventId, client, currentReaction, overlayHandle) {
  const existingGrid = picker.querySelector(".bryght-emoji-grid");
  if (existingGrid) {
    existingGrid.remove();
    return;
  }

  const grid = document.createElement("div");
  grid.className = "bryght-emoji-grid";

  EXTENDED_EMOJIS.forEach((emoji) => {
    const btn = document.createElement("button");
    btn.className = "bryght-reaction-picker-btn";
    if (currentReaction?.emoji === emoji) {
      btn.classList.add("active");
    }
    btn.textContent = emoji;
    btn.addEventListener("click", async () => {
      if (overlayHandle) overlayHandle.close();
      await toggleReaction(client, roomId, eventId, emoji);
    });
    grid.appendChild(btn);
  });

  picker.appendChild(grid);

  // Reposition the overlay now that the content is larger
  if (overlayHandle && overlayHandle.reposition) {
    overlayHandle.reposition();
  }
}

export function renderExistingReactions(targetEvent, reactionsEl, client) {
  const room = client.getRoom(targetEvent.getRoomId());
  if (!room) return;

  const targetId = targetEvent.getId();
  const myUserId = client.getUserId();
  const reactions = new Map();

  const timelineEvents = room.getLiveTimeline().getEvents();
  for (const ev of timelineEvents) {
    if (ev.getType() !== "m.reaction") continue;
    if (ev.isRedacted()) continue;
    const rel = ev.getContent()["m.relates_to"];
    if (rel?.event_id === targetId && rel?.key) {
      const existing = reactions.get(rel.key) || { count: 0, isMine: false };
      existing.count++;
      if (ev.getSender() === myUserId) existing.isMine = true;
      reactions.set(rel.key, existing);
    }
  }

  if (reactions.size === 0) return;

  reactions.forEach(({ count, isMine }, emoji) => {
    const badge = document.createElement("span");
    badge.className = "bryght-reaction-badge";
    if (isMine) badge.classList.add("bryght-reaction-mine");
    badge.textContent = `${emoji} ${count}`;
    reactionsEl.appendChild(badge);
  });
}

export function renderReactionOnMessage(reactionEvent, client) {
  const rel = reactionEvent.getContent()["m.relates_to"];
  const targetId = rel?.event_id;
  if (!targetId) return;

  const wrappers = document.querySelectorAll(`.bryght-message[data-event-id="${targetId}"]`);
  wrappers.forEach((wrapper) => {
    const container = wrapper.querySelector(".bryght-reactions");
    if (!container) return;
    container.innerHTML = "";
    if (wrapper._matrixEvent) {
      renderExistingReactions(wrapper._matrixEvent, container, client);
    }
  });
}
